import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAppDispatch } from '@application/store/useStore';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@iconify/react';
import VDKEventDocumentAPI from '@infrastructure/vdk-event-document-api';
import { alpha, Box, Button, ButtonBase, Checkbox, Divider, Typography, useTheme, Alert, IconButton, Avatar, Switch } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { HDatePicker, HTimePicker } from '@ui/components/common/Pickers';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import ControlledTextEditor from '@ui/components/TextEditor/ControlledTextEditor';
import VisuallyHiddenInput from '@ui/components/VisuallyHiddenInput';
import { useResponsive } from '@ui/style/useResponsive';
import { formatRegistrationDate, formatRegistrationTime, getRegistrationDateLocale } from '@application/utilities/registration-date-time';
import { DEFAULT_WELCOME_TEMPLATE, getDefaultThankYouText, getDefaultWelcomeTemplate, hasRichTextFormatting, replaceWelcomePlaceholders } from '@application/utilities/welcome-text-placeholders';
import { validateBackgroundImages } from '@application/helpers/background-image-helper';
import { isVideo } from '@application/helpers/video-detection-helper';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Controller, useController, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import useEventMetaFormStyles from './useEventMetaFormStyles';
import StyledAccordion from '@ui/components/Accordion/StyledAccordion';
import { Add, ClipboardText, Image, MagicStar, Profile2User, Security } from 'iconsax-react';
import { Iconly, Download } from 'react-iconly';
import ThemeToggle from '@ui/components/common/ThemeToggle';
import HugeIconsUpload from '@ui/assets/custom-icons/upload-icon';
import { PrimaryGrid } from '@ui/components/common/Grid';
import AddInformationDialog from './AddInformationDialog';
import TickCircle from '@ui/assets/custom-icons/tick-circle';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
const hasSectionError = (errors, sectionKeys) => {
    if (!errors)
        return false;
    const getErrorAtPath = (obj, path) => path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
    return sectionKeys.some((key) => !!getErrorAtPath(errors, key));
};
const stepOptions = Array.from({ length: 10 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString()
}));
const defaultStepTitleKeys = {
    1: 'eventSteps.personalInfoTitle',
    2: 'eventSteps.plusOneInfoTitle',
    3: 'eventSteps.transportTitle',
    4: 'eventSteps.accommodationTitle',
    5: 'eventSteps.miscTitle'
};
const defaultStepDescKeys = {
    1: 'eventSteps.personalInfoDesc',
    2: 'eventSteps.plusOneInfoDesc',
    3: 'eventSteps.transportDesc',
    4: 'eventSteps.accommodationDesc',
    5: 'eventSteps.miscDesc'
};
const isEditorContentEmpty = (value) => {
    if (typeof value !== 'string')
        return true;
    const plainText = value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
    return plainText === '';
};
const getDefaultStepName = (stepNumber, tFunction) => {
    return defaultStepTitleKeys[stepNumber]
        ? tFunction(defaultStepTitleKeys[stepNumber])
        : tFunction('eventSteps.defaultTitle', { step: stepNumber });
};
const getDefaultStepDescription = (stepNumber, tFunction) => {
    if (tFunction && defaultStepDescKeys[stepNumber]) {
        return tFunction(defaultStepDescKeys[stepNumber]);
    }
    return tFunction ? tFunction('eventSteps.defaultDesc') : '';
};
const getDefaultStepTitle = (stepNumber, tFunction) => {
    return getDefaultStepName(stepNumber, tFunction);
};
const DragableItem = ({ id, index, control, disabledStepName = false, onDelete, canDelete = true, translationLanguageCode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id
    });
    const theme = useTheme();
    const { t: tRaw, i18n } = useTranslation();
    const t = translationLanguageCode ? i18n.getFixedT(translationLanguageCode) : tRaw;
    const titleInputRef = useRef(null);
    const descInputRef = useRef(null);
    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        touchAction: 'none'
    };
    return (_jsx(Grid, { size: { xs: 12 }, ref: setNodeRef, style: style, children: _jsx(Box, { sx: { position: 'relative' }, children: _jsxs(Box, { sx: {
                    position: 'relative',
                    borderRadius: '14px',
                    backgroundColor: theme.palette.common.grey8,
                    border: `1px solid ${alpha(theme.palette.common.grey.main, 0.12)}`,
                    padding: '8px 12px',
                    display: 'flex',
                    flexDirection: 'column'
                }, onMouseDown: (e) => {
                    e.stopPropagation();
                }, onTouchStart: (e) => {
                    e.stopPropagation();
                }, children: [onDelete && (_jsx(Box, { onClick: (e) => {
                            if (!canDelete)
                                return;
                            e.stopPropagation();
                            onDelete();
                        }, sx: {
                            position: 'absolute',
                            top: 8,
                            right: 12,
                            cursor: canDelete ? 'pointer' : 'not-allowed',
                            color: canDelete ? theme.palette.error.main : theme.palette.action.disabled,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            pointerEvents: canDelete ? 'auto' : 'none',
                            opacity: canDelete ? 1 : 0.5,
                            '&:hover': canDelete
                                ? {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1)
                                }
                                : undefined
                        }, children: _jsx(Iconly, { name: 'Delete', set: 'light', style: { width: '20px', height: '20px' } }) })), _jsxs(Box, { ...attributes, ...listeners, sx: {
                            position: 'absolute',
                            top: 8,
                            left: 16,
                            cursor: 'grab',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&:active': {
                                cursor: 'grabbing'
                            }
                        }, children: [_jsxs(Box, { sx: { display: 'flex', gap: '2px' }, children: [_jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.text.secondary
                                        } }), _jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.text.secondary
                                        } })] }), _jsxs(Box, { sx: { display: 'flex', gap: '2px' }, children: [_jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.text.secondary
                                        } }), _jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.text.secondary
                                        } })] }), _jsxs(Box, { sx: { display: 'flex', gap: '2px' }, children: [_jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.action.active
                                        } }), _jsx(Box, { sx: {
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.text.secondary
                                        } })] })] }), _jsx(Typography, { variant: 'caption', sx: {
                            paddingLeft: '32px',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '22px',
                            color: theme.palette.primary.main
                        }, children: `${t('events.createNewEvent.step')} ${index + 1}` }), _jsx(Controller, { name: `steps.${index}.name`, control: control, render: ({ field }) => (_jsx("input", { ref: titleInputRef, type: 'text', value: String(field.value ?? ''), onChange: (e) => field.onChange(e.target.value), placeholder: t('eventSteps.defaultTitle', { step: index + 1 }), disabled: disabledStepName, style: {
                                paddingLeft: '32px',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '22px',
                                color: theme.palette.text.primary,
                                width: '100%',
                                fontFamily: 'inherit'
                            } })) }), _jsx(Controller, { name: `steps.${index}.description`, control: control, render: ({ field }) => (_jsx("input", { ref: descInputRef, type: 'text', value: String(field.value ?? ''), onChange: (e) => field.onChange(e.target.value), placeholder: t('eventSteps.defaultDesc'), disabled: disabledStepName, style: {
                                paddingLeft: '32px',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                ...theme.typography.caption,
                                color: theme.palette.common.grey.dark,
                                width: '100%',
                                fontFamily: 'inherit'
                            } })) })] }) }) }));
};
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || '';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const EventMetaForm = ({ control, watch, setValue, errors, companyOptions = [], collaboratorOptions = [], userOptions = [], interestOptions = [], languageOptions = [], eventLanguages, isEventDataLoaded = true, isLoadingEventData = false, isMetaLocked = false, onBackgroundImagesChange, backgroundImages: propBackgroundImages = [], onExtraInformationChange, initialExtraInformationRows, translationLanguageCode, onBeforeStepRemove, registeredCompanyIds = [] }) => {
    const dispatch = useAppDispatch();
    const { t: tRaw, i18n } = useTranslation();
    const t = translationLanguageCode ? i18n.getFixedT(translationLanguageCode) : tRaw;
    const dndContainerRef = useRef(null);
    const [activeId, setActiveId] = React.useState(null);
    const [activeTheme, setActiveTheme] = useState('preset');
    const [validationErrors, setValidationErrors] = useState([]);
    const [pendingCompanyRemoval, setPendingCompanyRemoval] = useState(null);
    // Use prop images if provided, otherwise maintain local state
    const backgroundImages = propBackgroundImages;
    const handleBackgroundImagesChange = useCallback((images) => {
        onBackgroundImagesChange?.(images);
    }, [onBackgroundImagesChange]);
    const [imageUrls, setImageUrls] = useState({});
    // NEW: sensor with a small activation distance to avoid accidental drags
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    // NEW: lock/unlock body scroll while dragging (prevents page scroll)
    const lockBodyScroll = () => {
        document.body.style.overflow = 'hidden';
    };
    const unlockBodyScroll = () => {
        document.body.style.overflow = '';
    };
    const theme = useTheme();
    const { isMobileIpadNavigation } = useResponsive();
    const regionGroups = React.useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    const defaultWelcomeTemplate = useMemo(() => getDefaultWelcomeTemplate(t), [t, i18n.language]);
    const defaultThankYouText = useMemo(() => getDefaultThankYouText(t), [t, i18n.language]);
    // Tracks the last confirmed 'companies' selection, so we can tell which company was just
    // removed (and revert to it if the user cancels the data-loss warning below).
    const companiesPrevValueRef = useRef([]);
    useEffect(() => {
        if (isEventDataLoaded) {
            companiesPrevValueRef.current = watch('companies') || [];
        }
    }, [isEventDataLoaded]);
    const handleCompanyChange = React.useCallback((newValue) => {
        const toId = (c) => (typeof c === 'object' && c.value ? c.value : c);
        const previousValue = companiesPrevValueRef.current;
        const newIds = (newValue || []).map(toId);
        const removedRegisteredIds = previousValue
            .map(toId)
            .filter((id) => !newIds.includes(id) && registeredCompanyIds.includes(id));
        if (removedRegisteredIds.length > 0) {
            setPendingCompanyRemoval({ previousValue, newValue: newValue || [] });
            return;
        }
        companiesPrevValueRef.current = newValue || [];
    }, [registeredCompanyIds]);
    // Handler for regional toggle in companies dropdown
    const handleCompanyRegionToggle = React.useCallback((regionLabel, field) => {
        const regionCompanyIds = regionGroups[regionLabel] || [];
        if (!regionCompanyIds.length)
            return;
        const currentCompanies = field.value || [];
        const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
        const allSelected = regionCompanyIds.every((id) => currentCompanyIds.includes(id));
        let updatedSelection;
        if (allSelected) {
            // Deselect all companies in this region
            updatedSelection = currentCompanies.filter((c) => {
                const id = typeof c === 'object' && c.value ? c.value : c;
                return !regionCompanyIds.includes(id);
            });
        }
        else {
            // Select all companies in this region (merge + de-duplicate)
            const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(opt.value) && !currentCompanyIds.includes(opt.value));
            updatedSelection = [...currentCompanies, ...newCompanies];
        }
        field.onChange(updatedSelection);
        handleCompanyChange(updatedSelection);
    }, [regionGroups, companyOptions, handleCompanyChange]);
    const eventDuration = watch('eventDuration');
    const startTime = watch('startTime');
    const endTime = watch('endTime');
    const location = watch('location');
    const welcomeText = watch('welcomeText');
    const thankYouText = watch('thankYouText');
    const previewLocale = useMemo(() => getRegistrationDateLocale(undefined, undefined, i18n.language ?? 'en'), [i18n.language]);
    const resolvedWelcomeText = useMemo(() => {
        const dateRange = formatRegistrationDate({ startDate: eventDuration?.startDate, endDate: eventDuration?.endDate }, previewLocale);
        const timeRange = formatRegistrationTime({
            startDate: eventDuration?.startDate,
            endDate: eventDuration?.endDate,
            startTime: startTime ?? undefined,
            endTime: endTime ?? undefined
        }, previewLocale);
        const template = welcomeText?.trim() || DEFAULT_WELCOME_TEMPLATE;
        return replaceWelcomePlaceholders(template, {
            location: location ?? '',
            dateRange,
            timeRange
        });
    }, [
        welcomeText,
        eventDuration?.startDate,
        eventDuration?.endDate,
        startTime,
        endTime,
        location,
        previewLocale
    ]);
    const welcomePlaceholderValues = useMemo(() => {
        const locale = getRegistrationDateLocale(undefined, undefined, i18n.language ?? 'en');
        const startDate = eventDuration?.startDate != null && String(eventDuration.startDate).trim() !== ''
            ? String(eventDuration.startDate).trim()
            : undefined;
        const endDate = eventDuration?.endDate != null && String(eventDuration.endDate).trim() !== ''
            ? String(eventDuration.endDate).trim()
            : undefined;
        const startTimeStr = startTime != null && startTime !== '' ? String(startTime) : undefined;
        const endTimeStr = endTime != null && endTime !== '' ? String(endTime) : undefined;
        const formattedTime = formatRegistrationTime({ startDate, endDate, startTime: startTimeStr, endTime: endTimeStr }, locale);
        // Use formatted time when available (locale-formatted e.g. "12:00 AM – 03:25 AM"); fallback only when it returns empty
        const timeRange = formattedTime && formattedTime.trim() !== ''
            ? formattedTime
            : startTimeStr || endTimeStr
                ? [startTimeStr, endTimeStr].filter(Boolean).join(' – ')
                : '';
        return {
            dateRange: formatRegistrationDate({ startDate, endDate }, locale),
            timeRange,
            location: (location ?? '').trim()
        };
    }, [
        eventDuration?.startDate,
        eventDuration?.endDate,
        startTime,
        endTime,
        location,
        i18n.language
    ]);
    const styles = useEventMetaFormStyles();
    const horizontal = !isMobileIpadNavigation;
    const noOfSteps = watch('noOfSteps');
    const isPasswordProtected = watch('passwordProtection');
    const steps = watch('steps');
    const { field: welcomeTextField } = useController({
        name: 'welcomeText',
        control
    });
    const { field: thankYouTextField } = useController({
        name: 'thankYouText',
        control
    });
    // WITH:
    const allDefaultWelcomeMessages = useMemo(() => {
        const msgs = new Set();
        msgs.add('Welcome to this event'); // English hardcoded fallback
        const langs = Object.keys(i18n.store?.data || {});
        langs.forEach((lang) => {
            const tLang = i18n.getFixedT(lang);
            const key = 'events.createNewEvent.defaultWelcomeMessage';
            if (i18n.exists(key, { lng: lang })) {
                msgs.add(String(tLang(key)));
            }
        });
        return msgs;
    }, [i18n.store?.data]);
    const allDefaultThankYouMessages = useMemo(() => {
        const msgs = new Set();
        msgs.add('Thank you for participating'); // English hardcoded fallback
        const langs = Object.keys(i18n.store?.data || {});
        langs.forEach((lang) => {
            const tLang = i18n.getFixedT(lang);
            const key = 'events.createNewEvent.defaultThankYouMessage';
            if (i18n.exists(key, { lng: lang })) {
                msgs.add(String(tLang(key)));
            }
        });
        return msgs;
    }, [i18n.store?.data]);
    const isStillDefaultWelcome = useCallback((value) => {
        if (hasRichTextFormatting(value))
            return false;
        const stripped = value
            .replace(/<p>\{\{[^}]+\}\}<\/p>/g, '')
            .replace(/<[^>]+>/g, '')
            .trim();
        return stripped === '' || allDefaultWelcomeMessages.has(stripped);
    }, [allDefaultWelcomeMessages]);
    const isStillDefaultThankYou = useCallback((value) => {
        if (hasRichTextFormatting(value))
            return false;
        const stripped = value.replace(/<[^>]+>/g, '').trim();
        return stripped === '' || allDefaultThankYouMessages.has(stripped);
    }, [allDefaultThankYouMessages]);
    useEffect(() => {
        if (!isEventDataLoaded)
            return;
        if (!isEditorContentEmpty(welcomeText) && !isStillDefaultWelcome(welcomeText))
            return;
        welcomeTextField.onChange(defaultWelcomeTemplate);
    }, [isEventDataLoaded, defaultWelcomeTemplate, i18n.language]);
    useEffect(() => {
        if (!isEventDataLoaded)
            return;
        if (!isEditorContentEmpty(thankYouText) && !isStillDefaultThankYou(thankYouText))
            return;
        thankYouTextField.onChange(defaultThankYouText);
    }, [isEventDataLoaded, defaultThankYouText, i18n.language]);
    const skipNextEndDateValidationRef = useRef(false);
    const { field: isMultiDayEventField } = useController({
        name: 'isMultiDayEvent',
        control
    });
    const isMultiDayEvent = Boolean(isMultiDayEventField.value);
    const { field: startDateField, fieldState: startDateFieldState } = useController({
        name: 'eventDuration.startDate',
        control,
        rules: {
            required: t('validations.required', { field: t('labels.startDate') }),
            validate: (value) => {
                if (!value || value === '') {
                    return t('validations.required', { field: t('labels.startDate') });
                }
                return true;
            }
        }
    });
    const { field: endDateField, fieldState: endDateFieldState } = useController({
        name: 'eventDuration.endDate',
        control,
        rules: {
            validate: (value) => {
                if (skipNextEndDateValidationRef.current) {
                    skipNextEndDateValidationRef.current = false;
                    return true;
                }
                if (isMultiDayEvent && (!value || value === '')) {
                    return t('validations.required', { field: t('labels.endDate') });
                }
                if (isMultiDayEvent && value <= startDateField.value) {
                    return t('validations.dateNotPast', { field: t('labels.endDate') });
                }
                return true;
            }
        }
    });
    const { fields, move, append, remove, update } = useFieldArray({
        control,
        name: 'steps',
        keyName: 'id'
    });
    // Validate that at least one step exists
    useEffect(() => {
        if (fields.length === 0 && isEventDataLoaded) {
            // Ensure at least one step exists
            console.warn('No steps found. At least one step is required.');
        }
    }, [fields.length, isEventDataLoaded]);
    // Sync noOfSteps with actual steps array length
    useEffect(() => {
        const currentLength = fields.length;
        const currentNoOfSteps = watch('noOfSteps');
        if (currentLength > 0 && currentLength.toString() !== currentNoOfSteps) {
            // Update noOfSteps to match the actual steps length
            control._defaultValues.noOfSteps = currentLength.toString();
            control._formValues.noOfSteps = currentLength.toString();
        }
    }, [fields.length, control, watch]);
    const externalsAllowedOptions = [
        { label: t('events.createNewEvent.yes'), value: true },
        { label: t('events.createNewEvent.no'), value: false }
    ];
    const passwordProtectionOptions = [
        { label: t('events.createNewEvent.yes'), value: true },
        { label: t('events.createNewEvent.no'), value: false }
    ];
    useEffect(() => {
        const activeImage = backgroundImages.find((img) => img.isBackgroundImage);
        if (activeImage?.isCustomImage) {
            setActiveTheme('custom');
        }
    }, [backgroundImages]);
    useEffect(() => {
        // Only manipulate steps if event data is loaded and there are no steps yet
        if (!isEventDataLoaded || fields.length > 0)
            return;
        // Initialize with 5 default steps
        for (let i = 0; i < 5; i++) {
            const stepNumber = i + 1;
            append({
                id: uuidv4(),
                name: getDefaultStepName(stepNumber, t),
                description: getDefaultStepDescription(stepNumber, t)
            });
        }
    }, [append, fields.length, isEventDataLoaded]);
    useEffect(() => {
        if (!isEventDataLoaded)
            return;
        fields.forEach((field, index) => {
            const stepNumber = index + 1;
            const currentName = String(field?.name ?? '').trim();
            const legacyDefault = `Step ${stepNumber}`;
            if (currentName === '' || currentName === legacyDefault) {
                update(index, {
                    ...field,
                    name: getDefaultStepName(stepNumber, t),
                    description: field?.description || getDefaultStepDescription(stepNumber, t)
                });
            }
        });
    }, [fields.length, isEventDataLoaded, t]);
    // Create object URLs for images and clean up old ones
    // Use a Map to track blob URLs by file signature
    const blobUrlMapRef = useRef(new Map());
    useEffect(() => {
        const newUrls = {};
        const currentFileKeys = new Set();
        backgroundImages.forEach((image) => {
            if (image.file) {
                const key = `${image.file.name}-${image.file.size}-${image.file.lastModified}`;
                currentFileKeys.add(key);
                // Reuse existing blob URL if already created, otherwise create new one
                if (blobUrlMapRef.current.has(key)) {
                    newUrls[key] = blobUrlMapRef.current.get(key);
                }
                else {
                    const blobUrl = URL.createObjectURL(image.file);
                    blobUrlMapRef.current.set(key, blobUrl);
                    newUrls[key] = blobUrl;
                }
            }
            else if (image.url) {
                newUrls[image.url] = image.url;
            }
        });
        // Only revoke blob URLs for files that are no longer in backgroundImages
        blobUrlMapRef.current.forEach((url, key) => {
            if (!currentFileKeys.has(key)) {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
                blobUrlMapRef.current.delete(key);
            }
        });
        setImageUrls(newUrls);
    }, [backgroundImages]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            blobUrlMapRef.current.forEach((url) => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            blobUrlMapRef.current.clear();
        };
    }, []);
    const handleFileUpload = useCallback((event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0)
            return;
        const errors = validateBackgroundImages(files);
        setValidationErrors(errors);
        if (errors.length > 0) {
            return;
        }
        const newImages = files.map((file) => ({
            file,
            isSelected: false,
            isCustomImage: true
        }));
        if (backgroundImages.length === 0 && newImages.length > 0) {
            newImages[0].isSelected = true;
        }
        const updatedImages = [...backgroundImages, ...newImages];
        handleBackgroundImagesChange(updatedImages);
        event.target.value = '';
    }, [backgroundImages, handleBackgroundImagesChange]);
    const handleImageSelection = useCallback((index) => {
        const updatedImages = backgroundImages.map((image, i) => ({
            ...image,
            isSelected: i === index,
            isBackgroundImage: i === index
        }));
        handleBackgroundImagesChange(updatedImages);
    }, [backgroundImages, handleBackgroundImagesChange]);
    const handleImageRemove = useCallback((index) => {
        if (backgroundImages.length <= 1) {
            return;
        }
        const updatedImages = backgroundImages.filter((_, i) => i !== index);
        if (backgroundImages[index]?.isSelected && updatedImages.length > 0) {
            updatedImages[0].isSelected = true;
            updatedImages[0].isBackgroundImage = true;
        }
        handleBackgroundImagesChange(updatedImages);
    }, [backgroundImages, handleBackgroundImagesChange]);
    const selectedImage = backgroundImages.find((img) => img.isSelected);
    const imageUrl = selectedImage?.file
        ? imageUrls[`${selectedImage.file.name}-${selectedImage.file.size}-${selectedImage.file.lastModified}`]
        : selectedImage?.url;
    const isVideoSelected = isVideo(selectedImage?.file?.type, selectedImage?.url);
    const displayImages = backgroundImages.length > 0 ? backgroundImages : [];
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = fields.findIndex((item) => item.id === active.id);
            const newIndex = fields.findIndex((item) => item.id === over?.id);
            move(oldIndex, newIndex);
        }
    };
    const fileToDataUrl = (file) => {
        return new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string')
                    res(reader.result);
                else
                    rej('Failed to read file');
            };
            reader.onerror = rej;
            reader.readAsDataURL(file);
        });
    };
    const handleImageUpload = async (file) => {
        var Url = await fileToDataUrl(file);
        try {
            await VDKEventDocumentAPI.uploadDocument({ FloorPlan: file }).then((response) => {
                Url = response.data.url;
            });
            return Url;
        }
        catch (err) {
            toast.error(t('events.imageUploadFailed') || 'Failed to upload image. Please try again.');
            throw err;
        }
    };
    const defaultSensors = useSensors(useSensor(PointerSensor));
    const [addInfoDialogOpen, setAddInfoDialogOpen] = useState(false);
    const [extraInformationRows, setExtraInformationRows] = useState(initialExtraInformationRows ?? []);
    const [editingRow, setEditingRow] = React.useState(null);
    useEffect(() => {
        //if initial data is present, then setExtraInformationRows
        if (initialExtraInformationRows &&
            initialExtraInformationRows.length > 0 &&
            extraInformationRows.length === 0) {
            setExtraInformationRows(initialExtraInformationRows);
        }
    }, [initialExtraInformationRows]);
    const handleAddInfoDialogOpen = () => {
        setEditingRow(null);
        setAddInfoDialogOpen(true);
    };
    const handleAddInfoDialogClose = () => setAddInfoDialogOpen(false);
    const handleAddInfoDialogSubmit = (data) => {
        setExtraInformationRows((prev) => {
            const updated = [
                ...prev,
                {
                    id: uuidv4(),
                    title: data.title,
                    description: data.description,
                    file: data.file ?? null,
                    fileurl: null,
                    documentname: data.file instanceof File ? data.file.name : null
                }
            ];
            return updated;
        });
        setEditingRow(null);
        setAddInfoDialogOpen(false);
    };
    const handleAddInfoDialogEdit = (data) => {
        if (!editingRow)
            return;
        setExtraInformationRows((prev) => prev.map((row) => {
            if (row.id === editingRow.id) {
                // When updating, if a new file is provided, replace the old one
                // If no new file, keep the existing fileurl if it exists
                return {
                    ...row,
                    title: data.title,
                    description: data.description,
                    file: data.file ?? null,
                    // Clear fileurl if a new file is uploaded
                    fileurl: data.file ? null : row.fileurl,
                    documentname: data.file
                        ? data.file instanceof File
                            ? data.file.name
                            : null
                        : row.documentname
                };
            }
            return row;
        }));
        setEditingRow(null);
        setAddInfoDialogOpen(false);
    };
    useEffect(() => {
        onExtraInformationChange?.(extraInformationRows);
    }, [extraInformationRows, onExtraInformationChange]);
    const timeToMinutes = (value) => {
        if (!value)
            return 0;
        const date = new Date(value);
        return date.getHours() * 60 + date.getMinutes();
    };
    return (_jsxs(Grid, { container: true, size: 12, spacing: 1.5, sx: styles.innerScrollableContentContainer, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { defaultExpanded: true, icon: _jsx(MagicStar, { variant: 'Outline', width: 20, height: 20, color: theme.palette.primary.main }), title: t('events.createNewEvent.eventDetailsCapsD'), subtitle: t('events.createNewEvent.eventOverviewScheduleLanguage'), sx: hasSectionError(errors, [
                        'eventTitle',
                        'companies',
                        'externalsAllowed',
                        'eventDuration.startDate',
                        'eventDuration.endDate',
                        'startTime',
                        'endTime'
                    ])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 1.5, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '6px', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.eventIdentity') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.eventIdentity'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'eventTitle', control: control, label: t('labels.eventTitle') + '*', labelTooltipText: t('events.createNewEvent.tooltips.eventTitle'), disabled: isMetaLocked, errorMessage: errors.eventTitle?.message, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.eventTitle')
                                        }),
                                        minLength: {
                                            value: 3,
                                            message: t('validations.minLength', {
                                                field: t('labels.eventTitle'),
                                                min: 3
                                            })
                                        },
                                        maxLength: {
                                            value: 100,
                                            message: t('validations.maxLength', {
                                                field: t('labels.eventTitle'),
                                                max: 100
                                            })
                                        }
                                    } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'location', control: control, label: t('labels.location'), labelTooltipText: t('events.createNewEvent.tooltips.location'), placeholder: t('labels.location'), disabled: isMetaLocked, errorMessage: errors.location?.message }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'companies', control: control, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.company')
                                        }),
                                        validate: (value) => {
                                            if (!value || value.length === 0) {
                                                return t('validations.required', {
                                                    field: t('labels.company')
                                                });
                                            }
                                            return true;
                                        }
                                    }, render: ({ field }) => {
                                        const currentCompanies = field.value || [];
                                        const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
                                        const optionIndexMap = new Map((companyOptions || []).map((option, index) => [option.value, index]));
                                        const groupOrderMap = new Map();
                                        (companyOptions || []).forEach((option, index) => {
                                            const group = option.regionLabel || '';
                                            if (!groupOrderMap.has(group)) {
                                                groupOrderMap.set(group, index);
                                            }
                                        });
                                        const sortedCompanyOptions = [...(companyOptions || [])].sort((a, b) => {
                                            const groupA = a.regionLabel || '';
                                            const groupB = b.regionLabel || '';
                                            const groupOrderA = groupOrderMap.get(groupA) ?? Number.MAX_SAFE_INTEGER;
                                            const groupOrderB = groupOrderMap.get(groupB) ?? Number.MAX_SAFE_INTEGER;
                                            if (groupOrderA !== groupOrderB)
                                                return groupOrderA - groupOrderB;
                                            const aSelected = currentCompanyIds.includes(a.value);
                                            const bSelected = currentCompanyIds.includes(b.value);
                                            if (aSelected !== bSelected)
                                                return aSelected ? -1 : 1;
                                            return ((optionIndexMap.get(a.value) ?? 0) - (optionIndexMap.get(b.value) ?? 0));
                                        });
                                        return (_jsx(ControlledHCheckboxAutocomplete, { control: control, ...field, onChange: handleCompanyChange, bg: 'grey8', options: sortedCompanyOptions, label: t('labels.company'), labelTooltipText: t('events.createNewEvent.tooltips.company'), error: errors.companies?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), disabled: isMetaLocked, maxTags: 1, 
                                            // Group companies by region
                                            groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                                                const regionLabel = params.group;
                                                const regionCompanyIds = regionGroups[regionLabel] || [];
                                                const allSelected = regionCompanyIds.length > 0 &&
                                                    regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                                                const someSelected = !allSelected &&
                                                    regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                                                const isUngrouped = !regionLabel;
                                                return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, disabled: isMetaLocked, onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        if (!isMetaLocked) {
                                                                            handleCompanyRegionToggle(regionLabel, field);
                                                                        }
                                                                    }, sx: {
                                                                        '&.Mui-disabled': {
                                                                            opacity: 0.5
                                                                        }
                                                                    } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                                            } }));
                                    } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(SingleSelect, { name: 'externalsAllowed', label: t('labels.externalsAllowed'), labelTooltipText: t('events.createNewEvent.tooltips.externalsAllowed'), options: externalsAllowedOptions, control: control, bg: 'grey8', disabled: isMetaLocked, error: errors.externalsAllowed?.message, rules: {
                                        validate: (value) => {
                                            if (value === null || value === undefined) {
                                                return t('validations.required', {
                                                    field: t('labels.externalsAllowed')
                                                });
                                            }
                                            return true;
                                        }
                                    } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Divider, {}) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '6px', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.schedule') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.schedule'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsxs(Box, { display: 'flex', alignItems: 'center', children: [_jsx(Box, { children: _jsx(Switch, { checked: isMultiDayEvent, disabled: isMetaLocked, onChange: (_, checked) => {
                                                            isMultiDayEventField.onChange(checked);
                                                            if (!checked) {
                                                                skipNextEndDateValidationRef.current = true;
                                                                const startDate = startDateField.value || '';
                                                                endDateField.onChange(startDate);
                                                            }
                                                            else {
                                                                endDateField.onChange('');
                                                            }
                                                        } }) }), _jsx(Typography, { variant: 'body2', color: theme.palette.text.primary, children: t('labels.multipleDayEvent') })] }), isMultiDayEvent ? (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HDatePicker, { value: startDateField.value || '', handleSelectedDate: (date) => {
                                                            if (isMetaLocked)
                                                                return;
                                                            startDateField.onChange(date || '');
                                                        }, label: t('labels.startDate') + '*', bg: 'grey8', readOnly: isMetaLocked, maxDate: endDateField.value ? new Date(endDateField.value) : undefined }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HDatePicker, { value: endDateField.value || '', handleSelectedDate: (date) => {
                                                            if (isMetaLocked)
                                                                return;
                                                            endDateField.onChange(date || '');
                                                        }, label: t('labels.endDate') + '*', bg: 'grey8', readOnly: isMetaLocked, minDate: startDateField.value
                                                            ? (() => {
                                                                const d = new Date(startDateField.value);
                                                                d.setDate(d.getDate() + 1);
                                                                return d;
                                                            })()
                                                            : undefined }) })] })) : (_jsx(HDatePicker, { value: startDateField.value || '', handleSelectedDate: (date) => {
                                                if (isMetaLocked)
                                                    return;
                                                const selectedDate = date || '';
                                                startDateField.onChange(selectedDate);
                                                endDateField.onChange(selectedDate);
                                            }, label: t('labels.eventDay') + '*', bg: 'grey8', readOnly: isMetaLocked })), (startDateFieldState.error || endDateFieldState.error) && (_jsx(FormValidationMessage, { error: startDateFieldState.error?.message ||
                                                endDateFieldState.error?.message ||
                                                t('events.dateRangeRequired') }))] }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'startTime', control: control, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.startTime')
                                        }),
                                        validate: (value) => {
                                            if (!value || value === null || value === '') {
                                                return t('validations.required', {
                                                    field: t('labels.startTime')
                                                });
                                            }
                                            return true;
                                        }
                                    }, render: ({ field, fieldState }) => {
                                        return (_jsxs(Box, { children: [_jsx(HTimePicker, { value: field.value, handleSelectedTime: (time) => {
                                                        if (!isMetaLocked) {
                                                            field.onChange(time);
                                                        }
                                                    }, label: t('labels.startTime') + '*', labelTooltipText: t('events.createNewEvent.tooltips.startTime'), bg: 'grey8', readOnly: isMetaLocked }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] }));
                                    } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'endTime', control: control, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.endTime')
                                        }),
                                        validate: (value) => {
                                            if (!value || value === null || value === '') {
                                                return t('validations.required', {
                                                    field: t('labels.endTime')
                                                });
                                            }
                                            if (!isMultiDayEvent && startTime && value) {
                                                const start = timeToMinutes(startTime);
                                                const end = timeToMinutes(value);
                                                if (end <= start) {
                                                    return t('validations.timeNotPast', {
                                                        field: t('labels.endTime')
                                                    });
                                                }
                                            }
                                            return true;
                                        }
                                    }, render: ({ field, fieldState }) => {
                                        return (_jsxs(Box, { children: [_jsx(HTimePicker, { value: field.value, handleSelectedTime: (time) => {
                                                        if (!isMetaLocked) {
                                                            field.onChange(time);
                                                        }
                                                    }, label: t('labels.endTime') + '*', labelTooltipText: t('events.createNewEvent.tooltips.endTime'), bg: 'grey8', readOnly: isMetaLocked }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] }));
                                    } }) })] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { icon: _jsx(Security, { variant: 'Outline', width: 20, height: 20, color: theme.palette.primary.main }), sx: hasSectionError(errors, [
                        'languageIds',
                        'primaryLanguageId',
                        'interests',
                        'contactEventTeamPhone',
                        'contactEventTeamEmail',
                        'passwordProtection',
                        'password'
                    ])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, title: t('events.createNewEvent.advanced'), subtitle: t('events.createNewEvent.advancedSubtitle'), children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 1.5, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '6px', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.misc') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.misc'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'languageIds', control: control, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.languages')
                                        }),
                                        validate: (value) => {
                                            if (!value || value.length === 0) {
                                                return t('validations.required', {
                                                    field: t('labels.languages')
                                                });
                                            }
                                            return true;
                                        }
                                    }, render: ({ field }) => {
                                        const primaryLanguageId = watch('primaryLanguageId');
                                        const selectedLanguages = field.value || [];
                                        // Enhance language options with primary indicator
                                        const enhancedLanguageOptions = languageOptions.map((option) => {
                                            const isPrimaryValue = Boolean(primaryLanguageId && String(option.value) === String(primaryLanguageId));
                                            return {
                                                label: option.label,
                                                value: option.value,
                                                iconUrl: option.iconUrl,
                                                isDisabled: option.isDisabled,
                                                isPrimary: isPrimaryValue ? true : undefined,
                                                primaryBadge: isPrimaryValue ? true : undefined,
                                                primaryLabel: isPrimaryValue ? t('labels.primary') || 'Primary' : undefined
                                            };
                                        });
                                        return (_jsxs(Box, { children: [_jsx(ControlledHCheckboxAutocomplete, { name: 'languageIds', control: control, bg: 'grey8', options: enhancedLanguageOptions, label: t('labels.languages') + '*', labelTooltipText: t('events.createNewEvent.tooltips.languages'), error: errors.languageIds?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll'), disabled: isMetaLocked }), selectedLanguages.length > 0 && (_jsx(Controller, { name: 'primaryLanguageId', control: control, rules: {
                                                        required: t('validations.required', {
                                                            field: t('labels.primaryLanguage') || 'Primary Language'
                                                        })
                                                    }, render: ({ field: primaryField, fieldState: primaryFieldState }) => {
                                                        return (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                                                                        mt: 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        flexWrap: 'wrap'
                                                                    }, children: [_jsxs(Typography, { variant: 'caption', sx: { color: theme.palette.text.secondary, fontWeight: 500 }, children: [t('labels.primaryLanguage') || 'Primary', ":", _jsx(Typography, { component: 'span', sx: { color: theme.palette.error.main, ml: 0.5 }, children: "*" })] }), selectedLanguages.map((lang) => {
                                                                            const langValue = typeof lang === 'object' && 'value' in lang
                                                                                ? lang.value
                                                                                : lang;
                                                                            const selectedOption = enhancedLanguageOptions.find((option) => String(option.value) === String(langValue));
                                                                            const langLabel = selectedOption?.label ||
                                                                                (typeof lang === 'object' && 'label' in lang
                                                                                    ? lang.label
                                                                                    : lang);
                                                                            const langIcon = selectedOption?.iconUrl ||
                                                                                (typeof lang === 'object' && 'iconUrl' in lang
                                                                                    ? lang.iconUrl
                                                                                    : undefined);
                                                                            const isSelected = primaryField.value === langValue;
                                                                            const cleanLabel = langLabel
                                                                                .replace(/\s*\(Primary\)\s*$/i, '')
                                                                                .replace(/\s*\(.*?\)\s*$/i, '');
                                                                            return (_jsxs(Box, { onClick: () => {
                                                                                    if (!isMetaLocked) {
                                                                                        primaryField.onChange(langValue);
                                                                                    }
                                                                                }, sx: {
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    px: 1,
                                                                                    py: 0.5,
                                                                                    borderRadius: 1,
                                                                                    cursor: isMetaLocked ? 'default' : 'pointer',
                                                                                    bgcolor: 'transparent',
                                                                                    border: isSelected
                                                                                        ? `1px solid ${theme.palette.primary.main}`
                                                                                        : `1px solid ${theme.palette.divider}`,
                                                                                    transition: 'all 0.2s ease',
                                                                                    '&:hover': {
                                                                                        bgcolor: 'transparent',
                                                                                        borderColor: isMetaLocked
                                                                                            ? theme.palette.divider
                                                                                            : theme.palette.primary.main
                                                                                    }
                                                                                }, children: [langIcon && (_jsx(Box, { component: 'img', src: langIcon, alt: cleanLabel, sx: {
                                                                                            width: 16,
                                                                                            height: 12,
                                                                                            objectFit: 'cover',
                                                                                            borderRadius: 0.5,
                                                                                            flexShrink: 0
                                                                                        } })), _jsx(Typography, { variant: 'caption', sx: {
                                                                                            fontWeight: isSelected ? 600 : 400,
                                                                                            color: isSelected
                                                                                                ? theme.palette.primary.main
                                                                                                : theme.palette.text.primary,
                                                                                            fontSize: '0.7rem',
                                                                                            whiteSpace: 'nowrap'
                                                                                        }, children: cleanLabel }), isSelected && (_jsx(Box, { sx: {
                                                                                            width: 5,
                                                                                            height: 5,
                                                                                            borderRadius: '50%',
                                                                                            bgcolor: theme.palette.primary.main,
                                                                                            flexShrink: 0
                                                                                        } }))] }, langValue));
                                                                        })] }), primaryFieldState?.error && (_jsx(FormValidationMessage, { error: primaryFieldState.error.message || '' }))] }));
                                                    } }))] }));
                                    } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { name: 'interests', control: control, bg: 'grey8', options: interestOptions, label: t('labels.tags'), labelTooltipText: t('events.createNewEvent.tooltips.tags'), error: errors.interests?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll'), disabled: isMetaLocked, rules: {} }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(PhoneInputMasking, { bg: 'grey8', control: control, name: 'contactEventTeamPhone', label: t('events.createNewEvent.contactEventTeamPhone'), disabled: isMetaLocked, error: errors.contactEventTeamPhone?.message, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.contactEventTeamPhone')
                                        })
                                    } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', control: control, name: 'contactEventTeamEmail', label: t('events.createNewEvent.contactEventTeamEmail'), labelTooltipText: t('events.createNewEvent.tooltips.contactEventTeamEmail'), placeholder: t('placeholders.contactEventTeamEmail'), disabled: isMetaLocked, errorMessage: errors.contactEventTeamEmail?.message, rules: {
                                        required: t('validations.required', {
                                            field: t('labels.contactEventTeamEmail')
                                        }),
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: t('validations.invalidEmail', {
                                                field: t('labels.contactEventTeamEmail')
                                            })
                                        }
                                    } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Divider, {}) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '6px', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.security') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.security'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12, sm: isPasswordProtected ? 6 : 12 }, children: _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'passwordProtection', label: t('labels.passwordProtection'), labelTooltipText: t('events.createNewEvent.tooltips.passwordProtection'), options: passwordProtectionOptions, disabled: isMetaLocked, error: errors.passwordProtection?.message, rules: {
                                        validate: (value) => {
                                            if (value === null || value === undefined) {
                                                return t('validations.required', {
                                                    field: t('labels.passwordProtection')
                                                });
                                            }
                                            return true;
                                        }
                                    } }) }), isPasswordProtected && (_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', control: control, name: 'password', label: t('labels.password') + '*', labelTooltipText: t('events.createNewEvent.tooltips.password'), placeholder: t('placeholders.setPasswordHere'), disabled: isMetaLocked, errorMessage: errors.password?.message, rules: {
                                        required: isPasswordProtected
                                            ? t('validations.required', { field: t('labels.password') })
                                            : false,
                                        minLength: isPasswordProtected
                                            ? {
                                                value: 6,
                                                message: t('validations.minLength', {
                                                    field: t('labels.password'),
                                                    min: 6
                                                })
                                            }
                                            : undefined,
                                        maxLength: isPasswordProtected
                                            ? {
                                                value: 20,
                                                message: t('validations.maxLength', {
                                                    field: t('labels.password'),
                                                    max: 20
                                                })
                                            }
                                            : undefined
                                    } }) }))] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { icon: _jsx(Icon, { icon: 'hugeicons:left-to-right-list-number', width: '20', height: '20', color: theme.palette.primary.main }), title: t('common.eventSteps'), subtitle: t('events.createNewEvent.registrationFlow'), sx: hasSectionError(errors, ['steps'])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 1.5, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.steps') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.eventSteps.steps'), variant: 'light', size: 'large' })] }) }), !isLoadingEventData && !isMetaLocked && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { ref: dndContainerRef, sx: {
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:has(.sortable-drag)': {
                                            touchAction: 'none'
                                        }
                                    }, children: _jsx(DndContext, { collisionDetection: closestCenter, sensors: sensors, autoScroll: true, onDragStart: (evt) => {
                                            // NEW handlers
                                            setActiveId(evt.active.id);
                                            lockBodyScroll();
                                        }, onDragCancel: () => {
                                            setActiveId(null);
                                            unlockBodyScroll();
                                        }, onDragEnd: (event) => {
                                            const { active, over } = event;
                                            setActiveId(null);
                                            unlockBodyScroll();
                                            if (!over || active.id === over.id)
                                                return;
                                            const oldIndex = fields.findIndex((f) => f.id === active.id);
                                            const newIndex = fields.findIndex((f) => f.id === over.id);
                                            move(oldIndex, newIndex);
                                        }, modifiers: [restrictToFirstScrollableAncestor, restrictToWindowEdges], children: _jsx(SortableContext, { items: fields.map((f) => f.id), strategy: verticalListSortingStrategy, children: _jsx(Grid, { container: true, spacing: 1, alignItems: 'center', wrap: 'wrap', flexDirection: { xs: 'column', lg: 'row' }, sx: {
                                                    overflowX: 'clip',
                                                    '&.sortable-drag-active': {
                                                        touchAction: 'none',
                                                        userSelect: 'none'
                                                    }
                                                }, children: fields.map((item, index) => (_jsx(React.Fragment, { children: _jsx(DragableItem, { id: item.id, index: index, control: control, disabledStepName: isMetaLocked, translationLanguageCode: translationLanguageCode, onDelete: () => {
                                                            onBeforeStepRemove?.(watch('steps') || []);
                                                            remove(index);
                                                        }, canDelete: fields.length > 1 }) }, item.id))) }) }) }) }) })), !isLoadingEventData && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', startIcon: isMetaLocked ? null : _jsx(Add, { variant: 'Outline' }), fullWidth: true, onClick: () => {
                                        const newStepNumber = fields.length + 1;
                                        append({
                                            id: uuidv4(),
                                            name: getDefaultStepName(newStepNumber, t),
                                            description: getDefaultStepDescription(newStepNumber, t)
                                        });
                                    }, disabled: fields.length >= 10 || isMetaLocked, children: isMetaLocked
                                        ? t('labels.numberOfStepsCount', { count: fields.length })
                                        : t('events.createNewEvent.addStep') }) }))] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { icon: _jsx(Profile2User, { variant: 'Outline', width: 20, height: 20, color: theme.palette.primary.main }), title: t('events.createNewEvent.managersAndSuppliers'), subtitle: t('events.createNewEvent.accessAndCollaborationSuppliersSettings'), sx: hasSectionError(errors, ['assigneeIds', 'collaboratorOptions'])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 1.5, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '6px', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.access') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.access'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', name: 'assigneeIds', control: control, options: userOptions, label: t('labels.organizer'), labelTooltipText: t('events.createNewEvent.tooltips.assignTo'), error: errors.assigneeIds?.message, showDesignation: false, showSelectAll: userOptions.length > 0, isSortable: true, selectAllLabel: t('actions.selectAll'), disabled: isMetaLocked }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', name: 'collaboratorIds', control: control, options: collaboratorOptions, label: t('tasks.collaborators'), labelTooltipText: t('events.createNewEvent.tooltips.suppliers'), error: errors.collaboratorOptions?.message, showDesignation: false, showSelectAll: collaboratorOptions.length > 0, isSortable: true, selectAllLabel: t('actions.selectAll'), disabled: isMetaLocked }) })] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { icon: _jsx(Icon, { icon: 'hugeicons:computer', width: '20', height: '20', color: theme.palette.primary.main }), title: t('events.createNewEvent.customizations'), subtitle: t('events.createNewEvent.customBackgroundWelcomeThankYouContent'), sx: hasSectionError(errors, ['welcomeText', 'thankYouText', 'backgroundImage'])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 3, children: [_jsxs(Grid, { container: true, size: 12, rowSpacing: 1, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.welcomeText') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.customization.welcomeText'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledTextEditor, { centerVariant: true, name: 'welcomeText', control: control, defaultValue: defaultWelcomeTemplate, placeholder: t('placeholders.startScreenContent'), error: errors.welcomeText?.message, readOnly: false, placeholderValues: welcomePlaceholderValues }, `welcome-${i18n.language}`) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.thankYouText') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.customization.thankYouText'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledTextEditor, { centerVariant: true, name: 'thankYouText', control: control, placeholder: t('placeholders.thankYouText'), error: errors.thankYouText?.message, readOnly: false }, `thankyou-${i18n.language}`) })] }), _jsxs(Grid, { container: true, size: 12, rowSpacing: 1, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('common.formBackground') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.customization.formBackground'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { display: 'flex', gap: 1, children: _jsx(ThemeToggle, { activeTheme: activeTheme, onThemeChange: (theme) => setActiveTheme(theme), disabled: isMetaLocked }) }) }), activeTheme === 'preset' && (_jsx(_Fragment, { children: _jsx(Grid, { size: { xs: 12 }, pt: 3, children: _jsx(Box, { display: 'flex', gap: 1, flexWrap: 'wrap', children: displayImages
                                                    .filter((image) => !image.isCustomImage)
                                                    .map((image, index) => {
                                                    let key;
                                                    let imgUrl;
                                                    if (image.file) {
                                                        key = `${image.file.name}-${image.file.size}-${image.file.lastModified}`;
                                                        imgUrl = imageUrls[key];
                                                    }
                                                    else if (image.url) {
                                                        key = image.url;
                                                        imgUrl = image.url;
                                                    }
                                                    else {
                                                        key = `background-image-${index}`;
                                                        imgUrl = undefined;
                                                    }
                                                    if (!imgUrl)
                                                        return null;
                                                    return (_jsxs(ButtonBase, { onClick: () => {
                                                            if (!isMetaLocked) {
                                                                handleImageSelection(displayImages.indexOf(image));
                                                            }
                                                        }, sx: {
                                                            width: 207,
                                                            height: 98,
                                                            borderRadius: '16px',
                                                            overflow: 'hidden',
                                                            position: 'relative'
                                                        }, children: [image.isSelected && (_jsx(TickCircle, { sx: { position: 'absolute', top: '4px', right: '5px' } })), isVideo(image.file?.type, image.url) ? (_jsx(Box, { component: 'video', src: imgUrl, muted: true, loop: true, playsInline: true, onMouseOver: (e) => {
                                                                    const video = e.currentTarget;
                                                                    if (video.paused)
                                                                        video.play().catch(() => { });
                                                                }, onMouseOut: (e) => {
                                                                    const video = e.currentTarget;
                                                                    if (!video.paused)
                                                                        video.pause();
                                                                }, sx: {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    // outline: image.isSelected
                                                                    //   ? `2px solid ${theme.palette.common.white}`
                                                                    //   : 'none',
                                                                    // outlineOffset: image.isSelected ? '-2.4px' : '0',
                                                                    border: image.isSelected
                                                                        ? `2px solid ${theme.palette.primary.main}`
                                                                        : 'none',
                                                                    objectFit: 'cover'
                                                                } })) : (_jsx(Box, { component: 'img', src: imgUrl, onError: (e) => {
                                                                    const target = e.target;
                                                                    target.src = '/assets/images/thumbnail/thumbnail_1.png';
                                                                }, sx: {
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '16px',
                                                                    // outline: image.isSelected
                                                                    //   ? `2px solid ${theme.palette.common.white}`
                                                                    //   : 'none',
                                                                    // outlineOffset: image.isSelected ? '-2.4px' : '0',
                                                                    objectFit: 'cover',
                                                                    border: image.isSelected
                                                                        ? `2px solid ${theme.palette.primary.main}`
                                                                        : 'none'
                                                                } })), backgroundImages.length > 1 && image.isCustomImage && (_jsx(Box, { sx: {
                                                                    position: 'absolute',
                                                                    bottom: 4,
                                                                    right: 6,
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                                    color: 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '12px',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.7)' }
                                                                }, onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    handleImageRemove(displayImages.indexOf(image));
                                                                }, children: "\u00D7" }))] }, key));
                                                }) }) }) })), activeTheme === 'custom' && (_jsxs(_Fragment, { children: [validationErrors.length > 0 && (_jsx(Grid, { size: { xs: 12 }, pt: 3, children: _jsx(Alert, { severity: 'error', children: validationErrors.map((error, index) => (_jsx("div", { children: error }, index))) }) })), _jsx(Grid, { size: { xs: 12 }, pt: 3, children: _jsxs(Box, { display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', children: [displayImages
                                                            .filter((image) => image.isCustomImage) // Only show uploaded images
                                                            .map((image, fileIndex) => {
                                                            const actualIndex = displayImages.indexOf(image);
                                                            let key;
                                                            let imgUrl;
                                                            if (image.file) {
                                                                key = `${image.file.name}-${image.file.size}-${image.file.lastModified}`;
                                                                imgUrl = imageUrls[key];
                                                            }
                                                            else if (image.url) {
                                                                key = image.url;
                                                                imgUrl = image.url;
                                                            }
                                                            else {
                                                                key = `background-image-${fileIndex}`;
                                                                imgUrl = undefined;
                                                            }
                                                            if (!imgUrl)
                                                                return null;
                                                            return (_jsxs(ButtonBase, { onClick: () => handleImageSelection(actualIndex), sx: {
                                                                    width: 207,
                                                                    height: 98,
                                                                    borderRadius: '16px',
                                                                    overflow: 'hidden',
                                                                    position: 'relative'
                                                                }, children: [image.isSelected && (_jsx(TickCircle, { sx: { position: 'absolute', top: '4px', right: '5px' } })), isVideo(image.file?.type, image.url) ? (_jsx(Box, { component: 'video', src: imgUrl, muted: true, loop: true, playsInline: true, onMouseOver: (e) => {
                                                                            const video = e.currentTarget;
                                                                            if (video.paused)
                                                                                video.play().catch(() => { });
                                                                        }, onMouseOut: (e) => {
                                                                            const video = e.currentTarget;
                                                                            if (!video.paused)
                                                                                video.pause();
                                                                        }, sx: {
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            // outline: image.isSelected
                                                                            //   ? `2px solid ${theme.palette.common.white}`
                                                                            //   : 'none',
                                                                            // outlineOffset: image.isSelected ? '-2.4px' : '0',
                                                                            border: image.isSelected
                                                                                ? `2px solid ${theme.palette.primary.main}`
                                                                                : 'none',
                                                                            objectFit: 'cover'
                                                                        } })) : (_jsx(Box, { component: 'img', src: imgUrl, onError: (e) => {
                                                                            const target = e.target;
                                                                            target.src = '/assets/images/thumbnail/thumbnail_1.png';
                                                                        }, sx: {
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            borderRadius: '16px',
                                                                            // outline: image.isSelected
                                                                            //   ? `2px solid ${theme.palette.common.white}`
                                                                            //   : 'none',
                                                                            // outlineOffset: image.isSelected ? '-2.4px' : '0',
                                                                            objectFit: 'cover',
                                                                            border: image.isSelected
                                                                                ? `2px solid ${theme.palette.primary.main}`
                                                                                : 'none'
                                                                        } })), _jsx(Box, { sx: {
                                                                            position: 'absolute',
                                                                            bottom: 4,
                                                                            right: 6,
                                                                            width: 20,
                                                                            height: 20,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                                            color: 'white',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '12px',
                                                                            cursor: 'pointer',
                                                                            '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.7)' }
                                                                        }, onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            handleImageRemove(actualIndex);
                                                                        }, children: "\u00D7" })] }, key));
                                                        }), !isMetaLocked && (_jsxs(Button, { variant: 'outlined', color: 'info', size: 'large', fullWidth: true, startIcon: _jsx(HugeIconsUpload, { color: theme.palette.info.main }), component: 'label', sx: {
                                                                border: `1px solid ${alpha(theme.palette.info.main, 0.48)}`,
                                                                color: theme.palette.info.main,
                                                                '&:hover': {
                                                                    backgroundColor: 'transparent',
                                                                    borderColor: alpha(theme.palette.info.main, 0.48),
                                                                    color: theme.palette.info.main
                                                                }
                                                            }, children: [t('actions.uploadNew'), _jsx(VisuallyHiddenInput, { type: 'file', multiple: true, accept: 'image/*,video/mp4', onChange: handleFileUpload })] }))] }) })] }))] })] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { title: t('events.createNewEvent.extraInformation'), subtitle: t('events.createNewEvent.optionalMediaAndEventDetails'), icon: _jsx(Image, { variant: 'Outline', width: 20, height: 20, color: theme.palette.primary.main }), sx: hasSectionError(errors, ['extraInformation'])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 0.75, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.extraItems') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.extraInformation.table'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PrimaryGrid, { showHeaderControls: false, showNewHeaderControls: false, hideFooter: true, style: { height: 'auto' }, noRowsText: t('events.createNewEvent.noExtraInformationRows'), columns: [
                                        {
                                            field: 'title',
                                            headerName: t('events.createNewEvent.title'),
                                            flex: 1,
                                            minWidth: 150
                                        },
                                        {
                                            field: 'description',
                                            headerName: t('events.createNewEvent.description'),
                                            flex: 2,
                                            minWidth: 250
                                        },
                                        {
                                            field: 'attachment',
                                            headerName: t('events.createNewEvent.attachment'),
                                            flex: 1,
                                            minWidth: 150,
                                            renderCell: (params) => {
                                                const file = params.row.file;
                                                const fileUrl = params.row.fileurl;
                                                const documentName = params.row.documentname;
                                                const isVideoUrl = (url) => {
                                                    const path = url?.split('?')[0] ?? ''; // strip SAS token query params
                                                    return (/\.(mp4|webm|ogg|mov)$/i.test(path) ||
                                                        /\.(mp4|webm|ogg|mov)$/i.test(documentName ?? ''));
                                                };
                                                const isVideoFile = (fileType) => fileType?.startsWith('video/') ?? false;
                                                const isPdfUrl = (url) => {
                                                    const path = url?.split('?')[0] ?? '';
                                                    // blob-proxy URLs have no extension in the path — fall back to documentName
                                                    return /\.pdf$/i.test(path) || /\.pdf$/i.test(documentName ?? '');
                                                };
                                                const isPdfFile = (fileType) => fileType === 'application/pdf';
                                                // New File upload
                                                if (file instanceof File) {
                                                    if (isVideoFile(file.type)) {
                                                        return (_jsx(Box, { component: 'video', src: URL.createObjectURL(file), sx: { width: 40, height: 40, objectFit: 'cover', borderRadius: 1 } }));
                                                    }
                                                    if (isPdfFile(file.type)) {
                                                        return (_jsx(Box, { sx: {
                                                                width: 40,
                                                                height: 40,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: 1,
                                                                bgcolor: alpha(theme.palette.error.main, 0.1)
                                                            }, children: _jsx(Icon, { icon: 'mdi:file-pdf-box', width: 28, height: 28, color: theme.palette.error.main }) }));
                                                    }
                                                    // Image file falls here
                                                    return (_jsx(Avatar, { variant: 'square', src: URL.createObjectURL(file), style: { width: '40px', height: '40px' }, alt: file.name }));
                                                }
                                                // URL from backend
                                                if (fileUrl) {
                                                    if (isVideoUrl(fileUrl)) {
                                                        // MP4, WebM, etc
                                                        return (_jsx(Box, { component: 'video', src: fileUrl, sx: { width: 40, height: 40, objectFit: 'cover', borderRadius: 1 } }));
                                                    }
                                                    if (isPdfUrl(fileUrl)) {
                                                        return (_jsx(Box, { sx: {
                                                                width: 40,
                                                                height: 40,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: 1,
                                                                bgcolor: alpha(theme.palette.error.main, 0.1)
                                                            }, children: _jsx(Icon, { icon: 'mdi:file-pdf-box', width: 28, height: 28, color: theme.palette.error.main }) }));
                                                    }
                                                    // Image URL (jpg, png, gif, webp, etc) falls here
                                                    return (_jsx(Avatar, { variant: 'square', src: fileUrl, style: { width: '40px', height: '40px' }, alt: documentName || 'Document' }));
                                                }
                                                // No attachment at all
                                                return (_jsx(Avatar, { variant: 'square', style: { width: '40px', height: '40px' }, children: _jsx(Iconly, { name: 'Document', set: 'light' }) }));
                                            }
                                        },
                                        {
                                            field: 'actions',
                                            headerName: '',
                                            flex: 0.5,
                                            minWidth: 160,
                                            sortable: false,
                                            disableColumnMenu: true,
                                            renderCell: (params) => (_jsxs(Box, { display: 'flex', gap: 0.5, alignItems: 'center', children: [_jsx(IconButton, { size: 'small', onClick: (e) => {
                                                            e.stopPropagation();
                                                            if (isMetaLocked)
                                                                return;
                                                            const row = extraInformationRows.find((r) => r.id === params.row.id);
                                                            if (row) {
                                                                // Pass file if it's a File object, otherwise pass the URL string
                                                                const fileToPass = row.file instanceof File
                                                                    ? row.file
                                                                    : row.fileurl
                                                                        ? row.fileurl
                                                                        : null;
                                                                setEditingRow({
                                                                    id: row.id,
                                                                    title: row.title,
                                                                    description: row.description,
                                                                    file: fileToPass,
                                                                    documentName: row.documentname
                                                                });
                                                                setAddInfoDialogOpen(true);
                                                            }
                                                        }, children: _jsx(Iconly, { name: 'Edit', set: 'light', size: 20 }) }), _jsx(IconButton, { size: 'small', onClick: (e) => {
                                                            e.stopPropagation();
                                                            if (isMetaLocked)
                                                                return;
                                                            setExtraInformationRows((prev) => prev.filter((row) => row.id !== params.row.id));
                                                        }, sx: { color: theme.palette.error.main }, children: _jsx(Iconly, { name: 'Delete', set: 'light', size: 20 }) }), (params.row.file instanceof File || params.row.fileurl) && (_jsx(IconButton, { size: 'small', onClick: (e) => {
                                                            e.stopPropagation();
                                                            const href = params.row.file instanceof File
                                                                ? URL.createObjectURL(params.row.file)
                                                                : params.row.fileurl;
                                                            const name = params.row.file instanceof File
                                                                ? params.row.file.name
                                                                : params.row.documentname || 'document';
                                                            const link = document.createElement('a');
                                                            link.href = href;
                                                            link.target = '_blank';
                                                            link.setAttribute('download', name);
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            if (params.row.file instanceof File) {
                                                                URL.revokeObjectURL(href);
                                                            }
                                                        }, children: _jsx(Download, { size: 20 }) }))] }))
                                        }
                                    ], rows: extraInformationRows.map((row) => ({
                                        id: row.id,
                                        title: row.title,
                                        description: row.description,
                                        attachment: row.file instanceof File ? row.file.name : row.documentname || '',
                                        file: row.file,
                                        fileurl: row.fileurl,
                                        documentname: row.documentname
                                    })) }) }), _jsx(Grid, { size: { xs: 12 }, pt: 3, children: _jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', fullWidth: true, startIcon: _jsx(Add, { variant: 'Outline' }), disabled: isMetaLocked, onClick: handleAddInfoDialogOpen, children: t('events.createNewEvent.addInformation') }) })] }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(StyledAccordion, { title: t('events.createNewEvent.internalInformation'), subtitle: t('events.createNewEvent.internalEventDetails'), icon: _jsx(ClipboardText, { variant: 'Outline', width: 20, height: 20, color: theme.palette.primary.main }), sx: hasSectionError(errors, ['details'])
                        ? { border: '1px solid', borderColor: theme.palette.error.main }
                        : undefined, children: _jsxs(Grid, { container: true, size: 12, rowSpacing: 1.5, columnSpacing: 1, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { display: 'flex', gap: '6px', alignItems: 'center', children: [_jsx(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: t('events.createNewEvent.description') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.internalInformation.description'), variant: 'light', size: 'large' })] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledTextEditor, { name: 'details', control: control, placeholder: t('placeholders.eventDetails'), readOnly: isMetaLocked, error: errors.details?.message }) })] }) }) }), _jsx(AddInformationDialog, { open: addInfoDialogOpen, onClose: handleAddInfoDialogClose, onSubmit: handleAddInfoDialogSubmit, onEdit: handleAddInfoDialogEdit, initialData: editingRow || undefined, isEdit: editingRow ? true : false }), _jsx(ConfirmationDialog, { open: !!pendingCompanyRemoval, onClose: () => {
                    if (pendingCompanyRemoval) {
                        setValue?.('companies', pendingCompanyRemoval.previousValue);
                    }
                    setPendingCompanyRemoval(null);
                }, title: t('events.createNewEvent.companyRemovalWarningTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setValue?.('companies', pendingCompanyRemoval?.previousValue ?? []);
                                setPendingCompanyRemoval(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', size: 'large', fullWidth: true, onClick: () => {
                                companiesPrevValueRef.current = pendingCompanyRemoval?.newValue ?? [];
                                setPendingCompanyRemoval(null);
                            }, children: t('events.createNewEvent.companyRemovalWarningContinue') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.createNewEvent.companyRemovalWarningMessage') }) }) })] }));
};
export default EventMetaForm;
